import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import StudentChipList from './StudentChipList';

type Student = {
    id: number;
    firstName: string;
    lastName: string;
};

type TestItem = {
    testId: string;
    name: string;
    type: string;
    assignDate?: string;
    numAssigned: number;
    numTaken: number;
    completedStudentIds?: number[];
    assignedStudentIds?: number[];
};

type QuestionSetItem = {
    questionSetId: number;
    name: string;
    type: string;
    subject: string;
    preset?: boolean;
    assignDate?: string;
    numAssigned: number;
    numTaken: number;
    completedStudentIds?: number[];
    assignedStudentIds?: number[];
};

type VocabItem = {
    id: number | string;
    name: string;
    words?: number;
    assignDate?: string;
    numAssigned: number;
    numTaken: number;
    completedStudentIds?: number[];
    assignedStudentIds?: number[];
};

type AssignmentTrackingProps = {
    testType: string; // SAT | PSAT | ACT | SHSAT — shared filter
    students: Student[];
    tests?: TestItem[];
    assignments?: QuestionSetItem[];
    vocab?: VocabItem[];
};

type Row = {
    id: string;
    name: string;
    kind: 'Test' | 'Assignment' | 'Vocab';
    subtype: string;
    section: string;
    testType: string | null; // 'SAT' | 'PSAT' | 'ACT' | 'SHSAT' | null (vocab)
    assignDate?: string;
    numAssigned: number;
    numTaken: number;
    completedStudentIds?: number[];
    assignedStudentIds?: number[];
};

const KIND_FILTERS = ['All', 'Test', 'Assignment', 'Vocab'] as const;

const stripDigital = (t: string) => (t || '').replace('Digital ', '');

const sectionPillCls = (section: string) =>
    section === 'Math'
        ? 'cdr__math'
        : section === 'R/W'
        ? 'cdr__rw'
        : section === 'Vocab'
        ? 'cdr__vocab'
        : 'cdr__mixed';

const compCls = (pct: number) =>
    pct >= 90 ? 'success' : pct >= 70 ? '' : pct >= 50 ? 'warning' : 'danger';

function AssignmentTracking({ testType, students, tests = [], assignments = [], vocab = [] }: AssignmentTrackingProps) {
    const [expanded, setExpanded] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [kind, setKind] = useState<typeof KIND_FILTERS[number]>('All');
    const [sortBy, setSortBy] = useState('assigned-desc');

    const rows = useMemo<Row[]>(() => {
        // Only show items actually assigned to THIS class. The tests/assignments
        // payload also carries the full catalog (shared with the assign-a-test UI);
        // class-assigned items are the ones with an assignDate (a GroupClassTest /
        // GroupClassQuestionSet exists), catalog-only items have none.
        const testRows: Row[] = tests.filter(t => !!t.assignDate).map(t => ({
            id: `t-${t.testId}`,
            name: t.name,
            kind: 'Test',
            subtype: stripDigital(t.type),
            section: 'Mixed',
            testType: stripDigital(t.type),
            assignDate: t.assignDate,
            numAssigned: t.numAssigned,
            numTaken: t.numTaken,
            completedStudentIds: t.completedStudentIds,
            assignedStudentIds: t.assignedStudentIds,
        }));

        const assignmentRows: Row[] = assignments.filter(a => !!a.assignDate).map(a => ({
            id: `a-${a.questionSetId}`,
            name: a.name,
            kind: 'Assignment',
            subtype: a.preset ? 'Preset' : 'Custom',
            section:
                a.subject === 'Math' || a.subject === 'Mathematics'
                    ? 'Math'
                    : a.subject === 'Reading and Writing' || a.subject === 'English Language Arts'
                    ? 'R/W'
                    : 'Mixed',
            testType: stripDigital(a.type),
            assignDate: a.assignDate,
            numAssigned: a.numAssigned,
            numTaken: a.numTaken,
            completedStudentIds: a.completedStudentIds,
            assignedStudentIds: a.assignedStudentIds,
        }));

        const vocabRows: Row[] = vocab.map(v => ({
            id: `v-${v.id}`,
            name: v.name,
            kind: 'Vocab',
            subtype: v.words ? `${v.words} words` : 'Vocab set',
            section: 'Vocab',
            testType: null,
            assignDate: v.assignDate,
            numAssigned: v.numAssigned,
            numTaken: v.numTaken,
            completedStudentIds: v.completedStudentIds,
            assignedStudentIds: v.assignedStudentIds,
        }));

        return [...testRows, ...assignmentRows, ...vocabRows];
    }, [tests, assignments, vocab]);

    // Vocab sets are SAT-only for now (we only provide SAT vocab), so they
    // surface just on the SAT tab. Hide the Vocab kind filter elsewhere and
    // drop the selection back to "All" so a non-SAT tab never lands on an
    // empty Vocab-filtered view.
    const isSat = testType === 'SAT';
    const kindFilters = isSat ? KIND_FILTERS : KIND_FILTERS.filter(k => k !== 'Vocab');
    useEffect(() => {
        if (!isSat && kind === 'Vocab') setKind('All');
    }, [isSat, kind]);

    const filtered = useMemo(() => {
        let out = rows.slice();
        // Shared SAT/PSAT/ACT filter applies to typed items. Vocab (testType null)
        // is gated to the SAT tab only, since SAT is the only vocab we provide.
        out = out.filter(r => r.testType === null ? isSat : r.testType === testType);
        if (kind !== 'All') out = out.filter(r => r.kind === kind);
        if (search.trim()) {
            const q = search.toLowerCase();
            out = out.filter(r => r.name.toLowerCase().includes(q));
        }
        out.sort((a, b) => {
            if (sortBy === 'assigned-desc') return (b.assignDate || '').localeCompare(a.assignDate || '');
            if (sortBy === 'assigned-asc') return (a.assignDate || '').localeCompare(b.assignDate || '');
            if (sortBy === 'completion') {
                const pa = a.numAssigned ? a.numTaken / a.numAssigned : 0;
                const pb = b.numAssigned ? b.numTaken / b.numAssigned : 0;
                return pb - pa;
            }
            if (sortBy === 'incomplete') {
                return (b.numAssigned - b.numTaken) - (a.numAssigned - a.numTaken);
            }
            return 0;
        });
        return out;
    }, [rows, testType, kind, search, sortBy]);

    const studentById = useMemo(() => {
        const m: { [id: number]: Student } = {};
        students.forEach(s => { m[s.id] = s; });
        return m;
    }, [students]);

    const nonCompleters = (r: Row): Student[] => {
        if (!r.assignedStudentIds || !r.completedStudentIds) return [];
        const done = new Set(r.completedStudentIds);
        return r.assignedStudentIds
            .filter(id => !done.has(id))
            .map(id => studentById[id])
            .filter(Boolean) as Student[];
    };

    return (
        <div className="cdr__tab-content">
            <div className="cdr__filterbar">
                <div className="cdr__filter-left">
                    <div className="cdr__search">
                        <input
                            placeholder="Search assignments..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="cdr__chip-group">
                        {kindFilters.map(k => (
                            <button
                                key={k}
                                className={kind === k ? 'active' : ''}
                                onClick={() => setKind(k)}
                            >
                                {k}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="cdr__filter-right">
                    <span className="cdr__sort-label">Sort by</span>
                    <select
                        className="cdr__select-like"
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                    >
                        <option value="assigned-desc">Newest first</option>
                        <option value="assigned-asc">Oldest first</option>
                        <option value="completion">Highest completion</option>
                        <option value="incomplete">Most incomplete</option>
                    </select>
                </div>
            </div>

            <div className="cdr__card cdr__card--flush">
                <table className="cdr__tbl">
                    <thead>
                        <tr>
                            <th style={{ width: '46%' }}>Name</th>
                            <th>Type</th>
                            <th>Assigned</th>
                            <th style={{ width: 240 }}>Completed</th>
                            <th style={{ width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={5} className="cdr__tbl-empty">
                                    No results match your filters.
                                </td>
                            </tr>
                        )}
                        {filtered.map(r => {
                            // Use org-ID-based arrays when available (deduplicated, consistent
                            // with the non-completer list). Fall back to raw counts otherwise.
                            const total = r.assignedStudentIds?.length ?? r.numAssigned;
                            const done = r.completedStudentIds?.length ?? r.numTaken;
                            const pct = total ? (done / total) * 100 : 0;
                            const isOpen = expanded === r.id;
                            const missing = nonCompleters(r);
                            const hasDetail = !!(r.assignedStudentIds && r.completedStudentIds);
                            return (
                                <React.Fragment key={r.id}>
                                    <tr
                                        className={`cdr__row${isOpen ? ' expanded' : ''}`}
                                        onClick={() => setExpanded(isOpen ? null : r.id)}
                                    >
                                        <td className="cdr__name-cell">
                                            <div>{r.name}</div>
                                            <div className="cdr__name-sub">{r.subtype}</div>
                                        </td>
                                        <td>
                                            <span className={`cdr__section-pill ${sectionPillCls(r.section)}`}>
                                                {r.kind}
                                            </span>
                                        </td>
                                        <td className="cdr__muted">
                                            {r.assignDate ? moment(r.assignDate).format('MMM D') : '—'}
                                        </td>
                                        <td>
                                            <div className="cdr__comp">
                                                <div className={`cdr__comp-bar ${compCls(pct)}`}>
                                                    <span style={{ width: `${pct}%` }}></span>
                                                </div>
                                                <span className="cdr__comp-text">
                                                    <strong>{done}</strong> / {total}
                                                </span>
                                            </div>
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <button
                                                className={`cdr__expand-btn${isOpen ? ' open' : ''}`}
                                                onClick={() => setExpanded(isOpen ? null : r.id)}
                                                aria-label="Show details"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="m6 9 6 6 6-6" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="cdr__detail">
                                            <td colSpan={5}>
                                                <div className="cdr__detail-panel">
                                                    {!hasDetail ? (
                                                        <div className="cdr__detail-note">
                                                            <strong>{r.numTaken}</strong> of {total} completed.
                                                            Per-student detail isn't available for this item.
                                                        </div>
                                                    ) : missing.length === 0 ? (
                                                        <div className="cdr__detail-note cdr__detail-note--done">
                                                            All assigned students completed this.
                                                        </div>
                                                    ) : (
                                                        <StudentChipList
                                                            header={<><strong>{missing.length}</strong> student{missing.length === 1 ? '' : 's'} haven't completed yet</>}
                                                            students={missing}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AssignmentTracking;
